<template>
  <transition name="fade-top-2x">
    <div
      v-if="value"
      class="premade-message-picker p-4 bg-white rounded-lg shadow-lg"
      style="z-index: 99999999"
    >
      <vs-row vs-justify="space-between" vs-align="start">
        <div>
          <h5>Premade messages</h5>
          <h6>{{ templateType | formatWord }} messages for {{ venue.name }}</h6>
          <small
            ><i>
              Click to view, edit and select premade messages.<br />Selecting a
              premade message will not send it
            </i>
          </small>
        </div>
        <feather-icon
          class="cursor-pointer"
          icon="XIcon"
          @click="$emit('input', false)"
        />
      </vs-row>
      <div
        v-if="isFetchingTemplates"
        class="flex flex-col justify-center items-center mt-12 pt-12"
      >
        <span class="flex h-4 w-4 mb-5">
          <span
            class="
              animate-ping
              absolute
              inline-flex
              h-4
              w-4
              rounded-full
              bg-primary
              opacity-75
            "
          ></span>
          <span
            class="relative inline-flex rounded-full h-4 w-4 bg-primary"
          ></span>
        </span>
        <span> Loading</span>
      </div>
      <vs-row v-else class="mt-4">
        <!-- Left side -->
        <vs-col
          vs-w="3"
          class="px-3 py-4 overflow-y-auto"
          style="max-height: 400px"
          vs-justify="center"
        >
          <div
            v-for="msg in messageTemplates"
            :key="msg.id"
            class="cursor-pointer w-full shadow-lg p-4 rounded-lg text-sm mb-2"
            :class="{
              'bg-black text-white':
                selected && selected.template.id == msg.template.id,
              'bg-white text-black':
                !selected || selected.template.id != msg.template.id,
            }"
            @click="selected = msg"
          >
            <b> {{ msg.template.title }} </b>
          </div>

          <div class="flex justify-center mt-4">
            <vs-button
              class="bg-primary-gradient"
              type="filled"
              @click="addNew"
              radius
              icon="add"
            ></vs-button>
          </div>
        </vs-col>

        <!-- Right side -->
        <vs-col vs-w="9" class="px-2">
          <vs-card v-if="selected">
            <div slot="header">
              <div class="flex justify-between">
                <h5 v-if="!isEditing">{{ selected.template.title }}</h5>
                <vs-input v-model="selected.template.title" v-else></vs-input>
                <div>
                  <feather-icon
                    class="cursor-pointer ml-4"
                    :icon="isEditing ? 'CheckIcon' : 'EditIcon'"
                    svgClasses="w-5 h-5"
                    @click="isEditing ? saveTemplate() : (isEditing = true)"
                  />
                  <feather-icon
                    v-if="!isEditing"
                    class="cursor-pointer ml-4"
                    icon="TrashIcon"
                    svgClasses="w-5 h-5"
                    @click="deleteSelected"
                  />
                  <feather-icon
                    v-if="isEditing"
                    class="cursor-pointer ml-4"
                    :icon="'XIcon'"
                    svgClasses="w-5 h-5"
                    @click="isEditing = !isEditing"
                  />
                </div>
              </div>
            </div>

            <div class="flex items-center">
              <div
                style="max-height: 180px; overflow-y: scroll"
                v-if="!isEditing && templateType == 'plain'"
                class="text-base flex-grow whitespace-pre-wrap"
                v-text="selected.renderedTemplate"
              ></div>
              <quill-editor
                :key="editorKey + 1000"
                v-if="!isEditing && templateType == 'html'"
                v-model="selected.renderedTemplate"
                :options="editorOptionDisabled"
                :disabled="true"
                class="flex-grow premade-quill"
              />
              <vs-textarea
                v-if="isEditing && templateType == 'plain'"
                class="premade-message-textarea"
                v-model="selected.template.template"
                ref="plainTextarea"
              ></vs-textarea>
              <quill-editor
                :key="editorKey"
                v-if="isEditing && templateType == 'html'"
                v-model="selected.template.template"
                :options="editorOption"
                class="flex-grow"
                ref="quillTextarea premade-quill"
              />

              <vs-button
                class="
                  bg-primary-gradient
                  ml-4
                  hover-animate-send
                  flex-shrink-0
                "
                :class="{ 'opacity-0': isEditing }"
                type="filled"
                id="chat-send-button"
                :disabled="isEditing"
                @click="
                  $emit('onSubmitMessage', selected.renderedTemplate);
                  $emit('input', false);
                "
                radius
                icon="send"
              ></vs-button>
            </div>
          </vs-card>
          <transition name="fade-top-2x">
            <div class="mt-3" v-if="isEditing">
              <vs-chip
                v-for="item in handlebars"
                :key="item.handlebar"
                class="cursor-pointer mr-2 mb-2"
                @click.native.stop.prevent="addHandlebar(item.handlebar)"
                @mousedown.prevent
              >
                <b>{{ item.name }}</b>
              </vs-chip>
            </div>
          </transition>
        </vs-col>
      </vs-row>
    </div>
  </transition>
</template>
    
<script>
import { mapGetters } from "vuex";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import { quillEditor } from "vue-quill-editor";

export default {
  components: {
    quillEditor,
  },
  props: ["value", "venueId", "templateType"],
  data() {
    return {
      selected: null,
      isEditing: false,
      editorKey: 0,
      editorOption: {
        modules: {
          toolbar: [
            [
              "bold",
              "italic",
              "underline",
              "strike",
              "link",
              "blockquote",
              "code-block",
            ],
            [{ header: 1 }, { header: 2 }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ font: [] }],
          ],
        },
        placeholder: "Type your message.",
      },
      editorOptionDisabled: {
        modules: {
          toolbar: [],
        },
      },
    };
  },
  watch: {
    isEditing(newValue) {
      this.editorKey++;
    },
    value(newValue, oldValue) {
      this.selected = null;
      this.isEditing = false;

      if (newValue) {
        this.isEditing = false;

        //   if (newValue && !this.messageTemplatesForVenue) { // Enable this check to only fetch once
        this.$store.dispatch("messageTemplates/getForVenue", this.venueId);
        //   }
      }
    },
  },
  computed: {
    ...mapGetters({
      isFetchingTemplates: "messageTemplates/isFetching",
    }),
    messageTemplates() {
      return this.$store.getters[
        `messageTemplates/messageTemplates_${this.templateType}`
      ];
    },

    venue() {
      return this.$store.getters["venues/venue"](this.venueId);
    },

    handlebars() {
      return this.$store.getters["handlebars/getFor"]("venue", "user")
        .map((e) => {
          return {
            ...e,
            handlebar: `{{${e.handlebar}}}`,
          };
        })
        .filter((e) =>
          e.handlebar == "{{venue_openhours}}" && this.templateType == "html"
            ? false
            : true
        )
        .filter((e) =>
          e.handlebar == "{{venue_openhours_html}}" &&
          this.templateType == "plain"
            ? false
            : true
        );
    },
  },
  methods: {
    addNew() {
      this.selected = {
        renderedTemplate: "",
        template: {
          id: null,
          title: "",
          description: "",
          templated: "",
          venueId: this.venueId,
          templateType: this.templateType == "plain" ? 0 : 1,
        },
      };
      this.isEditing = true;
    },
    async saveTemplate() {
      if (!this.selected) return;
      try {
        this.$vs.loading({ text: "Saving template..." });

        const isNew = this.selected.template.id == null;
        if (isNew) {
          const newId = await this.$store.dispatch(
            "messageTemplates/add",
            this.selected.template
          );
          this.selected.template.id = newId;
        } else {
          await this.$store.dispatch(
            "messageTemplates/update",
            this.selected.template
          );
        }

        this.$vs.notify({
          color: "green",
          title: `Message template ${isNew ? "created" : "updated"}`,
          text: "The message templates was successfully saved",
        });

        await this.$store.dispatch(
          "messageTemplates/getForVenue",
          this.venueId
        );

        console.log(
          "Finding for selected",
          this.selected.template.id,
          this.messageTemplates
        );
        // Update the rendered template
        var newTemplate = this.messageTemplates.find(
          (e) => e.template.id === this.selected.template.id
        );
        this.selected.renderedTemplate = newTemplate.renderedTemplate;

        this.isEditing = false;
      } catch (err) {
        console.error(err);

        this.$vs.notify({
          color: "danger",
          title: "Message templated could not be saved",
          text: err,
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    deleteSelected() {
      this.$vs.dialog({
        type: "confirm",
        color: "primary",
        title: `Confirm`,
        text: `Are you sure you want to delete this premade message template?`,
        accept: async () => {
          try {
            this.$vs.loading({ text: "Deleting template..." });
            await this.$store.dispatch(
              "messageTemplates/delete",
              this.selected.template.id
            );
            this.$vs.notify({
              color: "green",
              title: "Message template deleted",
              text: "The message template was successfully deleted",
            });

            await this.$store.dispatch(
              "messageTemplates/getForVenue",
              this.venueId
            );

            this.selected = null;
          } catch (err) {
            console.error(err);

            this.$vs.notify({
              color: "danger",
              title: "Message template not deleted",
              text: err,
            });
          } finally {
            this.$vs.loading.close();
          }
        },
      });
    },
    addHandlebar(handlebar) {
      if (this.templateType == "plain") {
        const el = this.$refs.plainTextarea.$el.children[0];
        const [start, end] = [el.selectionStart, el.selectionEnd];
        el.setRangeText(handlebar, start, end, "select");
        this.selected.template.template = el.value;
      } else {
        const selection = this.$refs.quillTextarea.quill.getSelection(true);
        this.$refs.quillTextarea.quill.insertText(
          selection == null ? 0 : selection.index,
          handlebar
        );
      }
    },
  },
};
</script>

<style lang="scss">
.ql-editor {
  max-height: 215px;
  overflow: auto;
}
.premade-message-textarea {
  margin-bottom: 0 !important;
  .vs-textarea {
    min-height: 64px;
    height: 128px;
    max-height: 300px;
    resize: vertical !important;
  }
}

.premade-message-picker {
  position: absolute;
  min-height: 400px;
  z-index: 9999;
  bottom: 0px;
  left: 0px;
  right: 0px;
  /*   bottom: 160px;
  left: 2rem;
  right: 2rem; */
  background-color: white;
}
</style>
